import { apiFetch, API_BASE_URL } from './apiBase';
import {
  normalizeWidgetAppointment,
  WIDGET_APPT_STORAGE_KEY,
} from '../utils/widgetAppointment';

function pickSpreadFallback(times) {
  if (!times.length) return [];
  if (times.length <= 4) return times;
  return [
    times[0],
    times[Math.floor(times.length * 0.33)],
    times[Math.floor(times.length * 0.66)],
    times[times.length - 1],
  ];
}

function safeTool(name, fn) {
  return async (params) => {
    try {
      return await fn(params || {});
    } catch (error) {
      console.error(`Client tool "${name}" failed:`, error);
      return JSON.stringify({
        error: error.message || String(error),
        tool: name,
        hint: 'Ensure npm run server is running on port 5000',
      });
    }
  };
}

/**
 * Names must match client tools on agent agent_8101ks3gqxk4ew8asewe2nvxs477
 */
export function createClientTools() {
  return {
    check_availability: safeTool('check_availability', async ({ date, time, preferred_time }) => {
      if (!date || date === 'undefined') {
        return JSON.stringify({
          success: false,
          error: 'Tarih gerekli (yyyy-MM-dd). Önce müşteriden günü alın.',
          agent_instruction: 'Müşteriye hangi gün istediğini sorun, sonra check_availability çağırın.',
        });
      }
      const preferred = time || preferred_time;
      let path = `/tools/availability?date=${encodeURIComponent(date)}`;
      if (preferred) path += `&time=${encodeURIComponent(preferred)}`;
      const data = await apiFetch(path);
      const slots = data.availableSlots || [];
      const times = data.availableTimes || slots.map((s) => s.time);
      const alternatives = data.suggestedAlternatives || [];
      const samples = data.sampleTimesToOffer || pickSpreadFallback(times);
      const requestedAvailable = data.requestedAvailable;
      let agent_instruction = data.agent_instruction || null;
      if (data.isServiceDay === false) {
        agent_instruction =
          (agent_instruction || '') +
          ' Bu gün kapalı. Başka bir iş günü için check_availability çağır.';
      } else if (requestedAvailable === true) {
        agent_instruction =
          (agent_instruction || '') +
          ` Saat ${data.requestedTime || preferred} müsait; bu saati onayla. suggestedAlternatives kullanma.`;
      } else if (requestedAvailable === false && alternatives.length) {
        agent_instruction =
          'İstenen saat dolu. Müşteriye "dolu" de; suggestedAlternatives içinden 2–4 saat öner; müşteri seçsin. Onay almadan book_appointment çağırma.';
      } else if (requestedAvailable === false && !alternatives.length) {
        agent_instruction = 'Bu gün tamamen dolu veya kapalı. Başka bir gün için check_availability çağır.';
      } else if (requestedAvailable === null && preferred) {
        // Saat gönderildi ama sunucu requestedAvailable dönmedi — koruyucu
        agent_instruction =
          'requestedAvailable alanı boş geldi. Bu saatin müsait olduğunu varsayma; check_availability aracını date ve time parametresiyle tekrar çağır.';
      } else if (requestedAvailable === null && !preferred) {
        // Sadece tarih gönderildi; ajan hâlâ saat sormadı
        agent_instruction =
          'Müşteriden tercih ettiği saati sor; ardından check_availability aracını date + time ile tekrar çağır. availableTimes listesindeki saatler müsait; müşteri bu listeden seçmeden book_appointment çağırma.';
      } else if (times.length === 0) {
        agent_instruction = 'Bu gün dolu; yakın başka bir gün için check_availability çağır.';
      }

      return JSON.stringify({
        date: data.date,
        requestedTime: data.requestedTime || preferred || null,
        requestedAvailable,
        availableCount: times.length,
        availableTimes: times.slice(0, 12),
        suggestedAlternatives: alternatives,
        sampleTimesToOffer: samples,
        spoken_summary:
          data.spoken_summary ||
          `${date} için ${times.length} müsait saat var.`,
        agent_instruction,
      });
    }),

    book_appointment: safeTool('book_appointment', async (params) => {
      const missing = ['date', 'time', 'plate', 'firstName', 'lastName'].filter(
        (k) => !params[k] && !(k === 'lastName' && params.last_name)
      );
      if (missing.length) {
        return JSON.stringify({
          success: false,
          error: `Eksik alanlar: ${missing.join(', ')}`,
          agent_instruction: 'Eksik bilgiyi müşteriden alın; onay almadan book_appointment çağırmayın.',
        });
      }
      const km = Number(params.kilometer ?? params.km);
      if (Number.isNaN(km) || km < 0) {
        return JSON.stringify({
          success: false,
          error: 'Geçerli kilometre bilgisi gerekli.',
          agent_instruction: 'Kilometreyi müşteriden alın.',
        });
      }
      const response = await fetch(`${API_BASE_URL}/appointments/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: params.date,
          time: params.time,
          plate: params.plate,
          customerPhone: params.customerPhone || params.phone,
          firstName: params.firstName,
          lastName: params.lastName,
          kilometer: km,
          chassisNumber: params.chassisNumber || 'Bilinmiyor',
          serviceType: params.serviceType || 'Bakım',
          additionalComplaints: params.additionalComplaints || '',
          confirmed: params.confirmed !== false,
          replacementCarRequested: Boolean(params.replacementCarRequested),
          valetRequested: Boolean(params.valetRequested),
          conversationId:
            params.conversationId ||
            (typeof window !== 'undefined' ? window.__mersoConversationId : null),
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json.success === false) {
        const alt = (json.availableSlots || [])
          .filter((s) => s.isAvailable !== false)
          .map((s) => s.time || s)
          .slice(0, 6);
        return JSON.stringify({
          success: false,
          booked: false,
          error: json.error || 'Randevu kaydedilemedi',
          requestedTime: params.time,
          suggestedAlternatives: alt,
          agent_instruction:
            alt.length > 0
              ? `${params.time} saati dolu, randevu KAYDEDİLMEDİ. Müşteriye "${params.time} dolu" de ve şu saatleri öner: ${alt.join(', ')}. Müşteri yeni saat seçince book_appointment'ı tekrar çağır.`
              : `${params.time} saati dolu, randevu KAYDEDİLMEDİ. Bu gün uygun saat yok; check_availability ile başka gün kontrol et.`,
        });
      }
      const appointment = json.data;
      const normalized = normalizeWidgetAppointment(appointment);
      if (normalized && typeof window !== 'undefined') {
        sessionStorage.setItem(WIDGET_APPT_STORAGE_KEY, JSON.stringify(normalized));
        window.dispatchEvent(
          new CustomEvent('merso-appointment-booked', { detail: normalized })
        );
      }
      return JSON.stringify({
        success: true,
        appointment: normalized,
        instruction:
          'Randevu kaydedildi. Müşteriye yalnızca kısa teşekkür et ve hatırlatma bilgisini ver; randevu özetini veya onay sorusunu TEKRAR OKUMA.',
        afternoon_note: appointment?.afternoonNoteRequired
          ? 'Öğleden sonra randevusu için süre notunu kısaca belirt.'
          : null,
        reminder:
          'Randevudan bir gün önce hatırlatma yapılacağını kısaca söyle.',
      });
    }),

    get_transfer_targets: safeTool('get_transfer_targets', async () => {
      const data = await apiFetch('/tools/transfer-targets');
      return JSON.stringify(data);
    }),

    log_transfer: safeTool('log_transfer', async (params) => {
      const response = await fetch(`${API_BASE_URL}/tools/log-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: params.conversationId,
          targetId: params.targetId,
          targetPhone: params.targetPhone,
          answered: Boolean(params.answered),
          completed: Boolean(params.completed),
        }),
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json.error || `HTTP ${response.status}`);
      }
      return JSON.stringify(json);
    }),

    get_scripts: safeTool('get_scripts', async () => {
      const data = await apiFetch('/tools/scripts');
      return JSON.stringify(data);
    }),
  };
}
